// `(treemap …)` tile op — part-to-whole breakdown at scale.
//
// `(treemap :label (col …) :value (col …) :span [W H] :group? (col …)
//           :colors? {…} :max-tiles? N)`
//
// One row of the resolved dataset per tile: `:label` names the tile, `:value`
// sizes its area. Optional `:group` adds one level of parent nesting.
// Registered in `dashboardMeta` (NOT `opMeta`) — only reachable as a child of
// `(dashboard …)`.

import { z } from 'zod';

import { categoryColorsArg, colRefArg, spanArg } from '../args.ts';
import { buildSpec, type DashboardCtx, dashboardMeta } from '../dispatch.ts';
import type { TreemapSpec } from '../value.ts';

export const treemap = z
  .object({
    name: z.literal('treemap'),
    params: z.tuple([]),
    span: spanArg,
    label: colRefArg,
    value: colRefArg,
    group: colRefArg.optional(),
    colors: categoryColorsArg.optional(),
    'max-tiles': z.number().int().min(1).optional(),
  })
  .describe(
    'A treemap for part-to-whole breakdown at scale. One row per tile: `:label` names the tile, `:value` ' +
      'sizes its area. Optional `:group` (col …) nests tiles one level under a parent rectangle. ' +
      '`:colors {:category token …}` overrides per-category colors; `:max-tiles N` collapses the smallest ' +
      'tiles past the top N into one "Other" tile.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: {
      expr: '(treemap :label (col rk well) :value (col rk oil) :span [6 5])',
    },
    apply: (data, ctx: DashboardCtx): Promise<TreemapSpec> =>
      buildSpec('treemap-spec', data, ctx, [
        { key: 'span', mode: 'copy' },
        { key: 'label', mode: 'col' },
        { key: 'value', mode: 'col' },
        { key: 'group', mode: 'optional-col' },
        { key: 'colors', mode: 'optional' },
        { key: 'maxTiles', from: 'max-tiles', mode: 'optional' },
      ]),
  });
